import classNames from "classnames";
import { Row } from "components/Projects";
import { Breadcrumbs, Header, ListGroup, ListGroupControl, LoadingState, View, Workspace } from "components/Shared";
import { useProjectsSearch } from "components/Projects/useProjectsSearch";
import Pagination from "components/Shared/Tcp/Pagination";
import { translate } from "lib";
import Datasource, { DatasourceResponse } from "lib/datasource";
import _ from "lodash";
import moment from "moment";
import React, { memo, useEffect, useRef, useState } from "react";
import axios from "v2/utils/axios";

/**
 * Pending Projects list (api-contract §5.2 `GET /v2/projects/pending`).
 * Property-admin scoped by the backend; each row continues the create-project
 * wizard at the "Scheduling" step via `/projects/:id/schedule` (F5).
 */
interface PendingFilterOptions {
	amendment_types_total: number;
	properties_total: number;
	amendment_types: { name: string; count: number }[];
	properties: { id: string; name: string; count: number }[];
}

const EMPTY_FILTERS: PendingFilterOptions = { amendment_types_total: 0, properties_total: 0, amendment_types: [], properties: [] };

const PENDING_SORTS = ["newest", "oldest", "name", "property_name", "amendment_type", "opening_date"];

const Pending = () => {
	const [projects, setProjects] = useState([] as tcpinpoint.Project[]);
	const [, renderTrigger] = useState(moment());
	const [datasource] = useState(
		new Datasource({
			mainModelName: "project",
			perPage: 20,
			currentPage: 1,
			sortBy: "newest",
			renderTrigger: renderTrigger,
		})
	);

	const { searchTerm, setSearchTerm, searchParams } = useProjectsSearch(datasource);
	const [amendmentType, setAmendmentType] = useState("");
	const [propertyId, setPropertyId] = useState("");
	const [filterOptions, setFilterOptions] = useState<PendingFilterOptions>(EMPTY_FILTERS);

	useEffect(() => {
		let cancelled = false;
		axios
			.get(`${process.env.REACT_APP_API_ENDPOINT_V2}projects/pending/filters`, {
				params: {
					...searchParams,
					...(amendmentType ? { amendment_type: amendmentType } : {}),
					...(propertyId ? { property_id: propertyId } : {})
				}
			})
			.then((res) => {
				if (!cancelled) setFilterOptions((res.data && res.data.data) || EMPTY_FILTERS);
			})
			.catch(() => undefined);
		return () => {
			cancelled = true;
		};
	}, [searchParams, amendmentType, propertyId]);

	const propertyNames = useRef<{ [id: string]: string }>({});
	filterOptions.properties.forEach((property) => {
		propertyNames.current[property.id] = property.name;
	});
	const propertyOptions =
		propertyId && !filterOptions.properties.some((property) => property.id === propertyId)
			? [...filterOptions.properties, { id: propertyId, name: propertyNames.current[propertyId] || translate("property"), count: 0 }]
			: filterOptions.properties;

	const typeOptions =
		amendmentType && !filterOptions.amendment_types.some((type) => type.name === amendmentType)
			? [...filterOptions.amendment_types, { name: amendmentType, count: 0 }]
			: filterOptions.amendment_types;

	const changeFilter = (setter: (value: string) => void) => (value: string) => {
		datasource.setCurrentPage(1);
		setter(value);
	};

	// subscribe datasource subject
	useEffect(() => {
		let sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) =>
			setProjects(response.normalizedMainModelResponse)
		);
		return () => sub.unsubscribe();
	}, [datasource]);

	// API call
	useEffect(() => {
		datasource.get("v2", "projects/pending", {
			queryStringParameters: {
				sort: datasource.sortBy,
				per_page: datasource.perPage,
				page: datasource.currentPage,
				include: JSON.stringify(["template"]),
				...searchParams,
				...(amendmentType ? { amendment_type: amendmentType } : {}),
				...(propertyId ? { property_id: propertyId } : {}),
			},
		});
	}, [datasource, datasource.currentPage, datasource.perPage, datasource.sortBy, searchParams, amendmentType, propertyId]);

	return (
		<React.Fragment>
			<Breadcrumbs items={[{ title: "projects", link: "/projects" }, { title: "pending_projects" }]} />
			<Header title="pending_projects" stamp="projects" />
			<Workspace id="pending-projects" className="list">
				<View resource="projects" hideTitle={true}>
					<ListGroupControl
						sortOptions={PENDING_SORTS}
						selectedSort={datasource.sortBy}
						onSortChange={(sortBy) => {
							datasource.setCurrentPage(1);
							datasource.setSortBy(sortBy);
						}}
						onSearchChange={setSearchTerm}
						searchValue={searchTerm}
						searchLabel="search_pending_projects"
					/>
					<div className="pending-filters">
						<label className="pending-filters__field">
							<span>{translate("amendment_type")}</span>
							<select value={amendmentType} onChange={(event) => changeFilter(setAmendmentType)(event.target.value)}>
								<option value="">
									{translate("all")} ({filterOptions.amendment_types_total})
								</option>
								{typeOptions.map((type) => (
									<option key={type.name} value={type.name}>
										{type.name} ({type.count})
									</option>
								))}
							</select>
						</label>
						<label className="pending-filters__field">
							<span>{translate("property")}</span>
							<select value={propertyId} onChange={(event) => changeFilter(setPropertyId)(event.target.value)}>
								<option value="">
									{translate("all")} ({filterOptions.properties_total})
								</option>
								{propertyOptions.map((property) => (
									<option key={property.id} value={property.id}>
										{property.name} ({property.count})
									</option>
								))}
							</select>
						</label>
						{(amendmentType || propertyId) && (
							<button
								type="button"
								className="pending-filters__clear"
								onClick={() => {
									datasource.setCurrentPage(1);
									setAmendmentType("");
									setPropertyId("");
								}}
							>
								{translate("clear_filters")}
							</button>
						)}
					</div>
					{datasource.isLoading ? (
						<LoadingState />
					) : _.isEmpty(projects) ? (
						<div className="alert alert-light empty-state" data-testid="pending-projects-empty-state">
							{translate(searchTerm.trim() || amendmentType || propertyId ? "no_matching_pending_projects" : "no_pending_projects")}
						</div>
					) : (
						<ListGroup
							resource="projects"
							collection={projects}
							pagination={false}
							renderRow={(project: tcpinpoint.Project) => (
								<Row
									key={project.id}
									project={project}
									showProperty={true}
									link={`/projects/${project.id}/schedule`}
									badge={translate("continue")}
								/>
							)}
						/>
					)}
					<div className={classNames("margin-atuo", "flex justify-center")}>
						<Pagination datasource={datasource} />
					</div>
				</View>
			</Workspace>
		</React.Fragment>
	);
};

export default memo(Pending);
